/**
 * Trazabilidad → Auditoría
 *
 * Quién generó o visualizó cada informe con datos sensibles, cuándo y desde
 * dónde. Los datos vienen de `auditoria.informes_accesos` vía
 * `/api/pdf/audit-log` (solo rol admin; ver api/pdf/SEGURIDAD-Y-AUDITORIA.md).
 *
 * El ítem del menú ya se oculta a los no-admin, pero eso es cosmético: quien
 * conozca la URL llega igual. Por eso el 403 del backend se muestra como estado
 * explícito en vez de tratarse como un error genérico.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { YoolTable, StatusPill } from '../../components/ui/YoolTable';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ShieldAlert, RefreshCw, AlertCircle, Eye, Download, Upload, Mail } from 'lucide-react';
import { fetchAuditLog } from '../../services/auditoriaService';

const PAGE_SIZE = 50;

/** Etiquetas e iconos por acción auditada (coinciden con el CHECK de la tabla). */
const ACCIONES = {
  download:                 { label: 'Descarga',            Icon: Download },
  preview:                  { label: 'Vista previa',        Icon: Eye },
  upload:                   { label: 'Subida',              Icon: Upload },
  upload_and_email:         { label: 'Subida + email',      Icon: Mail },
  upload_with_condicionado: { label: 'Subida + condicionado', Icon: Upload },
};

const TIPOS = {
  trazabilidad: 'Trazabilidad CMF',
  certificado:  'Certificado',
  cotizacion:   'Cotización',
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' });
};

const AuditoriaList = () => {
  const { getToken } = useAuth();

  const [registros, setRegistros] = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [denegado, setDenegado]   = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDenegado(false);
    try {
      const { registros: filas, total: t } = await fetchAuditLog(getToken, {
        limit:  PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRegistros(filas);
      setTotal(t);
    } catch (err) {
      if (err.status === 403) setDenegado(true);
      else setError(err.message || 'No se pudo cargar la auditoría');
      setRegistros([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [getToken, page]);

  useEffect(() => { cargar(); }, [cargar]);

  const columns = [
    {
      key: 'created_at',
      label: 'Fecha',
      sortable: true,
      render: (r) => <span className="whitespace-nowrap">{fmtFecha(r.created_at)}</span>,
    },
    {
      key: 'usuario_nombre',
      label: 'Usuario',
      sortable: true,
      // El nombre puede faltar (claim no configurado, o acceso por CRON_SECRET
      // en los flujos legados de §8.1): se degrada a email y luego a clerk_id.
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {r.usuario_nombre || r.usuario_email || (
              <span className="text-muted-foreground italic">Sin identificar</span>
            )}
          </span>
          {r.usuario_nombre && r.usuario_email && (
            <span className="text-xs text-muted-foreground">{r.usuario_email}</span>
          )}
        </div>
      ),
    },
    {
      key: 'tipo',
      label: 'Informe',
      sortable: true,
      render: (r) => TIPOS[r.tipo] || r.tipo,
    },
    {
      key: 'accion',
      label: 'Acción',
      sortable: true,
      render: (r) => {
        const a = ACCIONES[r.accion];
        if (!a) return r.accion;
        const { label, Icon } = a;
        return (
          <span className="inline-flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            {label}
          </span>
        );
      },
    },
    {
      key: 'contratante_nombre',
      label: 'Documento consultado',
      sortable: true,
      // El uuid solo no dice qué se revisó. El RPC resuelve el contratante y los
      // códigos; si no hay contexto (certificado/cotización, o una contratación
      // ya borrada) se cae al uuid, que sigue siendo el dato de auditoría.
      render: (r) => {
        if (!r.contratante_nombre && !r.codigo_suscripcion) {
          return (
            <code className="text-xs text-muted-foreground" title={r.recurso_id}>
              {String(r.recurso_id || '').slice(0, 8)}…
            </code>
          );
        }
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {r.contratante_nombre || 'Sin nombre'}
            </span>
            <span className="text-xs text-muted-foreground">
              {r.contratante_rut && <span className="font-mono">{r.contratante_rut}</span>}
              {r.contratante_rut && r.codigo_suscripcion && ' · '}
              {r.codigo_suscripcion && (
                <span className="font-mono" title="Código de suscripción">
                  {r.codigo_suscripcion}
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    { key: 'ip_address', label: 'IP', render: (r) => r.ip_address || '—' },
    {
      key: 'exito',
      label: 'Resultado',
      sortable: true,
      render: (r) =>
        r.exito ? (
          <StatusPill estado="completed">OK</StatusPill>
        ) : (
          // El detalle del error va en el title: la columna se satura si se
          // muestra siempre, y los fallos son minoría.
          <span title={r.error_detalle || 'Sin detalle'}>
            <StatusPill estado="fallido">Falló</StatusPill>
          </span>
        ),
    },
  ];

  if (denegado) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            Esta sección requiere rol de administrador. Si crees que deberías tener
            acceso, pide que te asignen el rol <code>admin</code> en Clerk.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const totalPaginas = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Auditoría de informes</CardTitle>
            <CardDescription>
              Quién generó o visualizó cada informe con datos sensibles, cuándo y desde
              dónde. El registro es append-only.
            </CardDescription>
          </div>
          {/* Sin filtro por tipo: en la práctica todo lo auditado es trazabilidad.
              Certificado y cotización se registran igual, pero sus flujos aún usan
              CRON_SECRET (§8.1) y entran anónimos — filtrarlos los escondería, que
              es lo contrario de lo que interesa. El backend sigue aceptando `tipo`
              por query si algún día se reactiva el selector. */}
          <Button variant="outline" size="icon" onClick={cargar} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <YoolTable
            columns={columns}
            rows={registros}
            rowKey="id"
            // Búsqueda en cliente: filtra la página cargada, no la tabla entera.
            search={{
              placeholder: 'Buscar por usuario, contratante, RUT o código…',
              keys: [
                'usuario_nombre', 'usuario_email',
                'contratante_nombre', 'contratante_rut',
                'codigo_suscripcion', 'codigo_contratacion',
                'ip_address',
              ],
            }}
            empty={loading ? 'Cargando…' : 'Sin accesos registrados todavía.'}
          />

          {totalPaginas > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {total} {total === 1 ? 'acceso' : 'accesos'} · página {page + 1} de {totalPaginas}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                  disabled={page === 0 || loading}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page + 1 >= totalPaginas || loading}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditoriaList;
